// Theme-aware brand logo selection shared across portals.
//
// Branding exposes up to four logo assets:
//   - logo_uri               → normal logo for the dark theme
//   - logo_light_uri         → normal logo for the light theme
//   - compact_logo_uri       → compact logo for the dark theme
//   - compact_logo_light_uri → compact logo for the light theme
//
// Selection is theme-aware with cross-fallback so a missing variant still
// resolves to a sensible asset, and the compact variant falls back to the
// normal logo when no compact asset is configured.

export type BrandLogoTheme = "light" | "dark";

export type BrandLogoVariant = "normal" | "compact";

export interface BrandLogoSources {
  logo_uri?: string | null;
  logo_light_uri?: string | null;
  compact_logo_uri?: string | null;
  compact_logo_light_uri?: string | null;
}

const firstNonEmpty = (...values: Array<string | null | undefined>): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim();
    }
  }
  return "";
};

/**
 * Resolve the normal (full) brand logo for the given theme, falling back to the
 * opposite theme's asset when the preferred one is not configured.
 */
export const selectNormalBrandLogo = (
  sources: BrandLogoSources,
  theme: BrandLogoTheme,
): string =>
  theme === "light"
    ? firstNonEmpty(sources.logo_light_uri, sources.logo_uri)
    : firstNonEmpty(sources.logo_uri, sources.logo_light_uri);

/**
 * Resolve the compact brand logo for the given theme. Falls back to the
 * opposite theme's compact asset, then to the normal logo for the same theme.
 */
export const selectCompactBrandLogo = (
  sources: BrandLogoSources,
  theme: BrandLogoTheme,
): string => {
  const compact =
    theme === "light"
      ? firstNonEmpty(sources.compact_logo_light_uri, sources.compact_logo_uri)
      : firstNonEmpty(sources.compact_logo_uri, sources.compact_logo_light_uri);

  return compact || selectNormalBrandLogo(sources, theme);
};

/**
 * Resolve a brand logo for the requested variant and theme.
 */
export const selectBrandLogo = (
  sources: BrandLogoSources,
  variant: BrandLogoVariant,
  theme: BrandLogoTheme,
): string =>
  variant === "compact"
    ? selectCompactBrandLogo(sources, theme)
    : selectNormalBrandLogo(sources, theme);
